import { useEffect, useRef } from "react";

interface CanvasSize {
  width: number;
  height: number;
}

interface UseFullscreenRefitOptions {
  isFullscreen: boolean;
  canvasSize: CanvasSize;
  resetFit: () => void;
  quietGapMs?: number;
  maxWaitMs?: number;
}

const sizesEqual = (a: CanvasSize, b: CanvasSize) => a.width === b.width && a.height === b.height;

/**
 * useFitToView locks in a fit (hasFitRef.current = true) 100ms after
 * canvasSize last changed, then ignores every canvasSize change after that.
 * Calling resetFit() the instant isFullscreen flips is unsafe: on a real,
 * OS-animated fullscreen transition the `fullscreenchange` event can fire
 * before the container's layout box has actually resized — canvasSize is
 * still the pre-transition value at that moment. Calling resetFit() right
 * then makes useFitToView fit to that stale size and lock in on it roughly
 * 100ms later, well before the real resize (which can take several hundred
 * ms on an animated OS transition) ever lands — so the real resize is then
 * silently ignored and the tree stays fit to the wrong container size.
 *
 * This hook instead waits for canvasSize to actually differ from its
 * pre-toggle value before calling resetFit() at all — that wait has no
 * timeout, so it's correct regardless of how long the real transition takes.
 * Once the first real change is observed, it re-calls resetFit() on every
 * subsequent canvasSize change for as long as those changes keep arriving
 * within quietGapMs of each other, absorbing any closely-spaced follow-up
 * layout ticks before letting useFitToView's own debounce take over and
 * lock in on the now-stable size. maxWaitMs is a safety net in case no
 * resize is ever observed after a toggle (e.g. fullscreen not actually
 * changing the container's box in some edge case) so the view doesn't get
 * stuck waiting forever.
 */
export const useFullscreenRefit = ({
  isFullscreen,
  canvasSize,
  resetFit,
  quietGapMs = 300,
  maxWaitMs = 3000,
}: UseFullscreenRefitOptions): void => {
  const hasMountedRef = useRef(false);
  const prevIsFullscreenRef = useRef(isFullscreen);
  const sizeAtToggleRef = useRef(canvasSize);
  const isWaitingForResizeRef = useRef(false);
  const isSettlingRef = useRef(false);
  const quietTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWaitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // On isFullscreen toggle: start waiting for the container to actually resize.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      prevIsFullscreenRef.current = isFullscreen;
      return;
    }
    if (prevIsFullscreenRef.current === isFullscreen) return;
    prevIsFullscreenRef.current = isFullscreen;

    sizeAtToggleRef.current = canvasSize;
    isWaitingForResizeRef.current = true;
    isSettlingRef.current = false;
    if (quietTimeoutRef.current !== null) {
      clearTimeout(quietTimeoutRef.current);
      quietTimeoutRef.current = null;
    }

    if (maxWaitTimeoutRef.current !== null) clearTimeout(maxWaitTimeoutRef.current);
    maxWaitTimeoutRef.current = setTimeout(() => {
      isWaitingForResizeRef.current = false;
      isSettlingRef.current = false;
      maxWaitTimeoutRef.current = null;
      resetFit();
    }, maxWaitMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);

  useEffect(() => {
    if (isWaitingForResizeRef.current) {
      if (sizesEqual(canvasSize, sizeAtToggleRef.current)) return;
      isWaitingForResizeRef.current = false;
      isSettlingRef.current = true;
      if (maxWaitTimeoutRef.current !== null) {
        clearTimeout(maxWaitTimeoutRef.current);
        maxWaitTimeoutRef.current = null;
      }
    }

    if (!isSettlingRef.current) return;

    resetFit();

    if (quietTimeoutRef.current !== null) clearTimeout(quietTimeoutRef.current);
    quietTimeoutRef.current = setTimeout(() => {
      isSettlingRef.current = false;
      quietTimeoutRef.current = null;
    }, quietGapMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasSize]);

  useEffect(() => {
    return () => {
      if (quietTimeoutRef.current !== null) clearTimeout(quietTimeoutRef.current);
      if (maxWaitTimeoutRef.current !== null) clearTimeout(maxWaitTimeoutRef.current);
    };
  }, []);
};
